import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Home, FolderOpen, FileText, Briefcase, Settings, LogOut, Sparkles } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { ThemeToggle } from '../../components';

const AdminContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
`;

const Sidebar = styled.nav`
  width: 250px;
  background-color: ${props => props.theme.colors.background.secondary};
  border-right: 1px solid ${props => props.theme.colors.border.light};
  padding: 1.5rem 0;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 0 1.5rem 2rem 1.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  margin-bottom: 2rem;

  h1 {
    font-size: ${props => props.theme.typography.h4};
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 0.5rem;
  }

  p {
    color: ${props => props.theme.colors.text.secondary};
    font-size: ${props => props.theme.typography.small};
    margin: 0;
  }
`;

const SidebarNav = styled.ul`
  list-style: none;
  flex: 1;
  padding: 0;
  margin: 0;
`;

const SidebarItem = styled.li`
  margin-bottom: 0.5rem;
`;

const SidebarLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text.secondary};
  text-decoration: none;
  font-weight: 500;
  transition: all ${props => props.theme.transitions.fast};
  background-color: ${props => props.active ? props.theme.colors.primaryLight : 'transparent'};
  border-right: 3px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};

  &:hover {
    background-color: ${props => props.theme.colors.primaryLight};
    color: ${props => props.theme.colors.primary};
  }

  svg {
    flex-shrink: 0;
  }
`;

const SidebarFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid ${props => props.theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: none;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background-color: ${props => props.theme.colors.background.tertiary};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const MainContent = styled.main`
  flex: 1;
  overflow-y: auto;
`;

const ContentHeader = styled.header`
  background-color: ${props => props.theme.colors.background.primary};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  padding: 1.5rem 2rem;

  h1 {
    color: ${props => props.theme.colors.text.primary};
    margin: 0;
  }
`;

const ContentBody = styled.div`
  padding: 2rem;
`;

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: Home, exact: true },
  { path: '/admin/projects', label: 'Projects', icon: FolderOpen },
  { path: '/admin/articles', label: 'Articles', icon: FileText },
  { path: '/admin/experiences', label: 'Experiences', icon: Briefcase },
  { path: '/admin/personalize', label: 'Personalize', icon: Sparkles },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

export const AdminLayout = ({ children, title = 'Admin Dashboard' }) => {
  const location = useLocation();

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    signOut(auth);
  };

  return (
    <AdminContainer>
      <Sidebar>
        <SidebarHeader>
          <h1>Portfolio CMS</h1>
          <p>Content Management</p>
        </SidebarHeader>

        <SidebarNav>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <SidebarItem key={item.path}>
                <SidebarLink
                  to={item.path}
                  active={isActive(item.path, item.exact)}
                >
                  <Icon size={18} />
                  {item.label}
                </SidebarLink>
              </SidebarItem>
            );
          })}
        </SidebarNav>

        <SidebarFooter>
          <ThemeToggle />
          <LogoutButton onClick={handleLogout}>
            <LogOut size={16} />
            Exit
          </LogoutButton>
        </SidebarFooter>
      </Sidebar>

      <MainContent>
        <ContentHeader>
          <h1>{title}</h1>
        </ContentHeader>
        <ContentBody>
          {children}
        </ContentBody>
      </MainContent>
    </AdminContainer>
  );
};